/**
 * Global session-expired / inactive-account handling via fetch interceptor.
 * On 401: try silent refresh (HttpOnly cookie) once, then retry the request.
 */

import { clearSession, refreshAccessToken, AUTH_FETCH_CREDENTIALS } from './swotifyAuth';
import { isSwotifyApiUrl } from './apiBase';

export const SessionEndReason = {
  EXPIRED: 'expired',
  INACTIVE: 'inactive',
};

let handler = null;
let originalFetch = null;
let fetchPatched = false;
let suppressUntil = 0;
// NOTE: there is deliberately no `modalOpen` latch here any more. It was a
// module-level flag set on the first session end and only cleared by the
// modal's own "Login again" button, so any other dismissal path left it stuck
// true and silently suppressed every later session end. The handler is an
// idempotent setState; re-invoking it is cheap and self-healing.

const INACTIVE_MARKERS = [
  'user account is inactive',
  'user not found or inactive',
  'role assignment not found or inactive',
];

export function registerSessionExpiredHandler(fn) {
  handler = fn;
  return () => {
    if (handler === fn) handler = null;
  };
}

/**
 * Retained as a no-op for callers that still announce modal state. The latch it
 * used to write is gone on purpose (see note above); nothing should depend on
 * suppressing session-end handling based on whether a dialog is showing.
 */
export function setSessionExpiredModalOpen(_open) {
  /* intentionally empty */
}

function isSuppressed() {
  if (Date.now() < suppressUntil) return true;
  try {
    return Date.now() < (window.__swotifySuppressSessionModalUntil || 0);
  } catch {
    return false;
  }
}

export function suppressSessionExpiredModal(ms = 4000) {
  suppressUntil = Date.now() + ms;
  try {
    if (typeof window !== 'undefined') {
      window.__swotifySuppressSessionModalUntil = suppressUntil;
    }
  } catch {
    /* ignore */
  }
}

/** True when the user is already on a screen that doesn't need a session. */
function onPublicAuthScreen() {
  try {
    if (typeof window === 'undefined') return false;
    const hash = window.location.hash || '';
    return /^#\/(login|terms|privacy|help)\b/.test(hash);
  } catch {
    return false;
  }
}

/** Last resort when no UI can prompt: drop the user on the login screen. */
function forceLoginRedirect() {
  try {
    if (typeof window === 'undefined') return;
    if (onPublicAuthScreen()) return; // already there — don't loop
    window.location.hash = '#/login';
  } catch {
    /* ignore */
  }
}

export function notifySessionExpired({ reason, message, detail } = {}) {
  if (isSuppressed()) return;

  const inactive =
    reason === SessionEndReason.INACTIVE ||
    INACTIVE_MARKERS.some((m) => String(detail || message || '').toLowerCase().includes(m));

  const resolvedReason = inactive ? SessionEndReason.INACTIVE : SessionEndReason.EXPIRED;
  const resolvedMessage =
    message ||
    (inactive
      ? 'Your account is inactive. Sign in again or contact your school administrator.'
      : 'Your session has expired for security. Please sign in again to continue.');

  // Clear FIRST and unconditionally. This used to sit behind `!handler`, so a
  // 401 arriving before SessionExpiredProvider registered its handler — i.e.
  // the burst of parallel requests a dashboard fires on mount — left the dead
  // token in localStorage and showed nothing. The user then browsed on with a
  // session the server had already rejected, collecting 401s on every page.
  clearSession();

  // Never cover the login screen with an "expired" dialog — it is a full-screen
  // blocking overlay, so it would sit on top of the sign-in form and stop the
  // user doing the one thing we are asking them to do.
  if (onPublicAuthScreen()) {
    return;
  }

  if (!handler) {
    // No UI available to ask nicely, so force it rather than failing silently.
    forceLoginRedirect();
    return;
  }

  // Always re-invoke: see the note on the removed `modalOpen` latch above.
  handler({
    reason: resolvedReason,
    message: resolvedMessage,
    detail: detail || '',
  });
}

function getRequestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof Request) return input.url;
  return input?.url || '';
}

function isPublicAuthPath(url) {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return (
      path.endsWith('/auth/login') ||
      path.endsWith('/auth/school-codes') ||
      path.endsWith('/auth/select-role') ||
      path.endsWith('/auth/refresh')
    );
  } catch {
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/school-codes') ||
      url.includes('/auth/select-role') ||
      url.includes('/auth/refresh')
    );
  }
}

function hasAuthContext(input, init) {
  try {
    const headers = new Headers(
      init?.headers || (input instanceof Request ? input.headers : undefined)
    );
    if (headers.get('Authorization')) return true;
  } catch {
    /* ignore */
  }
  try {
    return Boolean(localStorage.getItem('swotify_access_token'));
  } catch {
    return false;
  }
}

async function readDetail(response) {
  try {
    const data = await response.clone().json();
    if (typeof data?.detail === 'string') return data.detail;
    if (Array.isArray(data?.detail)) {
      return data.detail
        .map((x) => (typeof x === 'string' ? x : x?.msg || ''))
        .filter(Boolean)
        .join(', ');
    }
    return data?.message || '';
  } catch {
    return '';
  }
}

function isInactiveDetail(detail) {
  const d = String(detail || '').toLowerCase();
  return INACTIVE_MARKERS.some((m) => d.includes(m));
}

function shouldEndSession(status, detail) {
  if (status === 401) return true;
  if (status === 403 && isInactiveDetail(detail)) return true;
  return false;
}

function withCredentials(init) {
  if (!init) return { credentials: AUTH_FETCH_CREDENTIALS };
  if (init.credentials != null) return init;
  return { ...init, credentials: AUTH_FETCH_CREDENTIALS };
}

function rebuildInitWithAccessToken(input, init, accessToken) {
  const base = withCredentials(init || {});
  const headers = new Headers(
    base.headers || (input instanceof Request ? input.headers : undefined)
  );
  headers.set('Authorization', `Bearer ${accessToken}`);
  return { ...base, headers };
}

async function maybeHandleSessionEnd(input, init, response) {
  // Only `isSuppressed()` short-circuits here. A modal-open latch and a
  // `!handler` check used to gate this too, which meant a session ending before
  // the provider mounted was never cleared at all. notifySessionExpired() now
  // decides what to do in those cases.
  if (isSuppressed()) return;

  const url = getRequestUrl(input);
  if (!isSwotifyApiUrl(url) || isPublicAuthPath(url)) return;
  if (!hasAuthContext(input, init)) return;

  const { status } = response;
  const detail = await readDetail(response);
  if (!shouldEndSession(status, detail)) return;

  notifySessionExpired({
    reason: isInactiveDetail(detail) ? SessionEndReason.INACTIVE : SessionEndReason.EXPIRED,
    detail,
  });
}

export function installFetchSessionInterceptor() {
  if (fetchPatched || typeof window === 'undefined') return;

  originalFetch = window.fetch.bind(window);
  fetchPatched = true;

  window.fetch = async function swotifyFetch(input, init) {
    const url = getRequestUrl(input);
    const isApi = isSwotifyApiUrl(url);
    const nextInit =
      isApi && !url.includes('/auth/school-codes') ? withCredentials(init) : init;

    let response = await originalFetch(input, nextInit);

    const alreadyRetried = Boolean(nextInit?.headers && new Headers(nextInit.headers).get('X-Swotify-Retry'));
    if (
      response.status === 401 &&
      isApi &&
      !isPublicAuthPath(url) &&
      hasAuthContext(input, nextInit) &&
      !alreadyRetried
    ) {
      try {
        const newToken = await refreshAccessToken();
        const retryInit = rebuildInitWithAccessToken(input, nextInit, newToken);
        const headers = new Headers(retryInit.headers);
        headers.set('X-Swotify-Retry', '1');
        retryInit.headers = headers;
        response = await originalFetch(input, retryInit);
      } catch {
        /* fall through to session-end handling */
      }
    }

    void maybeHandleSessionEnd(input, nextInit, response);
    return response;
  };
}

/** Call from API helpers after fetch (uses response.url for path detection). */
export async function checkResponseSessionEnd(response) {
  const url = response?.url || '';
  await maybeHandleSessionEnd(url, null, response);
}

if (typeof window !== 'undefined') {
  installFetchSessionInterceptor();
}
